import { useMemo } from 'react';
import { Copy, Check } from 'lucide-react';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';

interface JsonBlockProps {
  label: string;
  value: unknown;
  className?: string;
}

export default function JsonBlock({ label, value, className = '' }: JsonBlockProps) {
  const { copied, copy } = useCopyToClipboard();
  // Memoize before the null guard: React requires hooks to run unconditionally
  // on every render. The guard goes below.
  const formatted = useMemo(() => JSON.stringify(value, null, 2), [value]);

  if (value === null || value === undefined) return null;

  return (
    <div className={`space-y-1 ${className}`}>
      <div className="flex items-center justify-between">
        <div className="text-xs font-medium text-muted-foreground">{label}</div>
        <button
          type="button"
          onClick={() => copy(formatted)}
          className="flex items-center gap-1 px-2 py-0.5 rounded text-xs text-muted-foreground hover:bg-muted transition-colors"
          aria-label={copied ? 'Copied' : 'Copy JSON to clipboard'}
          title={copied ? 'Copied!' : 'Copy JSON'}
        >
          {copied ? (
            <>
              <Check className="h-3 w-3 text-green-500" />
              <span className="text-green-500">Copied</span>
            </>
          ) : (
            <>
              <Copy className="h-3 w-3" />
              <span>Copy</span>
            </>
          )}
        </button>
      </div>
      <pre className="text-xs font-mono bg-muted/40 border border-border rounded p-2 overflow-auto max-h-80 whitespace-pre-wrap break-all">
        {formatted}
      </pre>
    </div>
  );
}
